import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Cart drawer upsell (`snippets/cart-drawer-upsell.liquid`).
 * Arrows page the card track; each card form is added with AJAX and the resulting section HTML is
 * handed to the cart components through `CartLinesUpdateEvent`, the same path the product form uses.
 */
class CartUpsell extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('submit', this.#handleSubmit, { signal });
    this.addEventListener('change', this.#handleVariantChange, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const arrow = event.target instanceof Element ? event.target.closest('[data-direction]') : null;
    const track = this.querySelector('.cart-upsell__track');
    if (!(arrow instanceof HTMLElement) || !(track instanceof HTMLElement)) return;

    const direction = Number(arrow.dataset.direction) * (document.dir === 'rtl' ? -1 : 1);
    track.scrollBy({ left: direction * track.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  };

  /** @param {Event} event */
  #handleVariantChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const form = select.form;
    const option = select.selectedOptions[0];
    if (!form || !option) return;
    const price = form.querySelector('[data-upsell-price]');
    const compare = form.querySelector('[data-upsell-compare]');
    const quantity = form.querySelector('input[name="quantity"]');
    if (price) price.textContent = option.dataset.price || '';
    if (compare instanceof HTMLElement) {
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
    }
    if (quantity instanceof HTMLInputElement) quantity.value = option.dataset.minimum || '1';
  };

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    if (button instanceof HTMLButtonElement && button.disabled) return;
    if (button instanceof HTMLButtonElement) button.disabled = true;
    const errorMessage = form.querySelector('.cart-upsell__error');
    if (errorMessage instanceof HTMLElement) errorMessage.hidden = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);
    const formData = new FormData(form);
    formData.append('sections', sectionIds.join(','));
    formData.append('sections_url', window.location.pathname);

    const deferred = CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(formData.get('id')), quantity: Number(formData.get('quantity')) || 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
        signal: this.#controller?.signal,
      });
      const added = await response.json();
      if (!response.ok || added.status) throw new Error(added.description || added.message || 'Add to cart failed');

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { signal: this.#controller?.signal });
      if (!cartResponse.ok) throw new Error('Cart refresh failed');
      const cart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'cart-upsell', sections: added.sections, didError: false },
      });
      if (button instanceof HTMLButtonElement) button.disabled = false;
    } catch (error) {
      deferred.reject(error);
      if (errorMessage instanceof HTMLElement && error?.name !== 'AbortError') {
        errorMessage.textContent = errorMessage.dataset.errorMessage || '';
        errorMessage.hidden = false;
      }
      if (button instanceof HTMLButtonElement) button.disabled = false;
    }
  };
}

if (!customElements.get('cart-upsell')) customElements.define('cart-upsell', CartUpsell);
